import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { message } from 'antd';

import { IAppUser, IReward, IVoucher } from '../../../interfaces';
import { EmitError } from '../../../utils/EmitError';
import { EmitSuccessAlert } from '../../../utils/EmitSuccessAlert';
import VouchersService from '../../../services/http/services/VouchersService';
import AppUsersService from '../../../services/http/services/AppUsersService';
import RewardsService from '../../../services/http/services/RewardsService';

export default function useViewVoucher() {
    const [isLoadingVoucher, setIsLoadingVoucher] = useState<boolean>(true);
    const [isLoadingAppUserAndRewardData, setIsLoadingAppUserAndRewardData] = useState<boolean>(false);
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [voucher, setVoucher] = useState<IVoucher | undefined>();
    const [appUser, setAppUser] = useState<IAppUser | undefined>();
    const [reward, setReward] = useState<IReward | undefined>();
    const [isRewardImageModalVisible, setIsRewardImageModalVisible] = useState<boolean>(false);
    const [isConfirmRedemptionModalVisible, setIsConfirmRedemptionModalVisible] = useState<boolean>(false);

    const { code } = useParams();
    const navigate = useNavigate();

    const loadVoucherData = useCallback(async (voucherCode: string, signal?: AbortSignal) => {
        try {
            setIsLoadingVoucher(true);
            const voucherData = await VouchersService.getVoucherByRedemptionCode({
                code: voucherCode,
                signal: signal as AbortSignal,
            });
            setVoucher(voucherData);
            return voucherData;
        } catch (error) {
            if (error instanceof AxiosError && error.name === 'CanceledError') {
                return null;
            }
            EmitError(error, true, 'viewVoucher_load_error');
            navigate('/vouchers');
            return null;
        } finally {
            setIsLoadingVoucher(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!code) {
            navigate('/vouchers');
            return;
        }

        const controller = new AbortController();
        loadVoucherData(code, controller.signal);

        return () => {
            controller.abort();
        };
    }, [code, loadVoucherData, navigate]);

    useEffect(() => {
        if (!voucher) return;

        const controller = new AbortController();

        async function loadAssociatedData() {
            try {
                setIsLoadingAppUserAndRewardData(true);

                const [appUserData, rewardData] = await Promise.all([
                    AppUsersService.getAppUserById({
                        appUserId: voucher?.app_user_id!,
                        signal: controller.signal,
                    }),
                    RewardsService.getRewardById(voucher?.reward_id!, controller.signal),
                ]);

                setAppUser(appUserData);
                setReward(rewardData);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'viewVoucher_associated_data_error');
            } finally {
                setIsLoadingAppUserAndRewardData(false);
            }
        }

        loadAssociatedData();

        return () => {
            controller.abort();
        };
    }, [voucher]);

    const handleConfirmRedemption = useCallback(() => {
        setIsConfirmRedemptionModalVisible(true);
    }, []);

    const handleCancel = useCallback(() => {
        setIsConfirmRedemptionModalVisible(false);
    }, []);

    const handleConfirm = useCallback(async () => {
        try {
            setIsSubmiting(true);
            if (!voucher) return;

            await VouchersService.confirmVoucherRedemption(voucher.id);
            EmitSuccessAlert('Entrega do voucher confirmada com sucesso!');

            setIsConfirmRedemptionModalVisible(false);

            if (code) {
                await loadVoucherData(code);
            }
        } catch (err) {
            EmitError(err, true, 'confirmVoucher_error');
        } finally {
            setIsSubmiting(false);
        }
    }, [voucher, code, loadVoucherData]);

    const handleClickViewReward = useCallback(() => {
        if (!reward) return;
        setIsRewardImageModalVisible(true);
    }, [reward]);

    const handleClickCloseRewardImageModal = useCallback(() => {
        setIsRewardImageModalVisible(false);
    }, []);

    const handleCopyCode = useCallback(() => {
        if (voucher?.redemption_code) {
            navigator.clipboard.writeText(voucher.redemption_code);
            message.success(`Código ${voucher.redemption_code} copiado!`);
        }
    }, [voucher]);

    const handleGoBack = useCallback(() => {
        navigate('/vouchers');
    }, [navigate]);

    const handleNavigateToAppUser = useCallback(() => {
        if (appUser?.id) {
            navigate(`/usuarios/aplicativo/visualizar/${appUser.id}`);
        }
    }, [appUser, navigate]);

    return {
        isLoadingVoucher,
        isLoadingAppUserAndRewardData,
        isSubmiting,
        voucher,
        appUser,
        reward,
        handleConfirmRedemption,
        handleClickViewReward,
        isRewardImageModalVisible,
        handleClickCloseRewardImageModal,
        handleConfirm,
        handleCancel,
        isConfirmRedemptionModalVisible,
        handleCopyCode,
        handleGoBack,
        handleNavigateToAppUser,
    };
}
